import type { Balance } from '@fanduel/cpe-um-bundle/wallet/contract';

import { isEmbeddedIframe, isEmbeddedQueryRequested } from '../embedded-mode';

export const WEB_WRAPPER_RPC = {
  CALL: 'WEB_WRAPPER/CALL',
  RESULT: 'WEB_WRAPPER/RESULT',
  TIMEOUT_MS: 30_000,
} as const;

export const WEB_WRAPPER_METHODS = {
  DISMISS_REALITY_CHECK_MODAL: 'dismissRealityCheckModal',
  GET_BALANCE: 'getBalance',
  GET_CONTEXT: 'getContext',
  GET_SESSION: 'getSession',
  GET_USER_ID: 'getUserId',
  NOTIFY_DETACH_READY: 'notifyDetachReady',
  OPEN_DEPOSIT: 'openDeposit',
  OPEN_RESPONSIBLE_PLAY_SETTINGS: 'openResponsiblePlaySettings',
  REQUEST_CLOSE: 'requestClose',
  REQUEST_DETACH: 'requestDetach',
  REQUEST_INCENTIVE_GAME_REDIRECT: 'requestIncentiveGameRedirect',
  REQUEST_KEEP_ALIVE: 'requestKeepAlive',
  REQUEST_LOGOUT: 'requestLogout',
  REQUEST_RELOAD: 'requestReload',
  TRIGGER_LOCATE_USER: 'triggerLocateUser',
  TRIGGER_LOCATE_USER_WITH_DOMAIN: 'triggerLocateUserWithDomain',
} as const;

type WebWrapperMethod =
  (typeof WEB_WRAPPER_METHODS)[keyof typeof WEB_WRAPPER_METHODS];

export type WebWrapperDetachPayload = {
  transferId: string;
  launcherUrl?: string;
  popupAlreadyOpened?: boolean;
};

export type WebWrapperDetachReadyPayload = {
  transferId: string;
};

export type WebWrapperSession = {
  jwt: string;
  sessionId: string;
  userId: number;
  loginToken?: string;
  created?: string;
};

export type WebWrapperContext = {
  embedded: true;
  region: string;
  geocomplyToken?: string;
  loginToken?: string;
};

export type WebWrapper = {
  isWrapper: () => boolean;
  triggerLocateUser: () => Promise<void>;
  triggerLocateUserWithDomain: (domain: string) => Promise<void>;
  getSession: () => Promise<WebWrapperSession | null>;
  getUserId: () => Promise<string | null>;
  getBalance: () => Promise<Set<Balance> | null>;
  getContext: () => Promise<WebWrapperContext | null>;
  openDeposit: () => Promise<void>;
  openResponsiblePlaySettings: () => Promise<void>;
  requestClose: () => void | Promise<void>;
  requestDetach: (payload: WebWrapperDetachPayload) => void | Promise<void>;
  notifyDetachReady: (payload: WebWrapperDetachReadyPayload) => Promise<void>;
  requestReload: () => Promise<void>;
  requestLogout: () => Promise<void>;
  requestKeepAlive: () => Promise<void>;
  requestIncentiveGameRedirect: (path: string) => Promise<void>;
  dismissRealityCheckModal: () => Promise<void>;
};

type WindowWithWebWrapper = Window & {
  WebWrapper?: WebWrapper;
};

type WebWrapperRpcResultMessage = {
  type: typeof WEB_WRAPPER_RPC.RESULT;
  id: string;
  result?: unknown;
  error?: string;
};

type PendingRpcCall = {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

let rpcListener: ((event: MessageEvent<unknown>) => void) | null = null;
let rpcIdCounter = 0;
const pendingRpcCalls = new Map<string, PendingRpcCall>();
let cachedRpcWrapper: WebWrapper | null = null;

/** Lobby window for messaging: detached opener first, embedded parent second. */
export const getLobbyWindowForMessaging = (): Window | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (window.opener && window.opener !== window) {
    return window.opener;
  }

  if (window.parent && window.parent !== window) {
    return window.parent;
  }

  return null;
};

const tryGetDirectWebWrapper = (target: Window | null): WebWrapper | null => {
  if (!target) {
    return null;
  }

  try {
    const wrapper = (target as WindowWithWebWrapper).WebWrapper;
    return wrapper?.isWrapper?.() ? wrapper : null;
  } catch {
    return null;
  }
};

const getDirectLobbyWebWrapper = (): WebWrapper | null =>
  tryGetDirectWebWrapper(window) ||
  tryGetDirectWebWrapper(getLobbyWindowForMessaging());

const getRpcTargetWindow = (): Window | null => {
  if (
    typeof window !== 'undefined' &&
    isEmbeddedIframe() &&
    window.parent !== window
  ) {
    return window.parent;
  }

  return null;
};

const isRpcResultMessage = (
  data: unknown,
): data is WebWrapperRpcResultMessage => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const candidate = data as Partial<WebWrapperRpcResultMessage>;
  return (
    candidate.type === WEB_WRAPPER_RPC.RESULT &&
    typeof candidate.id === 'string' &&
    (candidate.error === undefined || typeof candidate.error === 'string')
  );
};

const isRpcResultFromTarget = (event: MessageEvent<unknown>): boolean => {
  const target = getRpcTargetWindow();
  return (
    target !== null &&
    event.origin === window.location.origin &&
    event.source === target
  );
};

const installRpcListener = (): void => {
  if (rpcListener || typeof window === 'undefined') {
    return;
  }

  rpcListener = (event: MessageEvent<unknown>): void => {
    if (!isRpcResultFromTarget(event) || !isRpcResultMessage(event.data)) {
      return;
    }

    const pending = pendingRpcCalls.get(event.data.id);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timer);
    pendingRpcCalls.delete(event.data.id);

    if (event.data.error) {
      pending.reject(new Error(event.data.error));
      return;
    }

    pending.resolve(event.data.result);
  };

  window.addEventListener('message', rpcListener);
};

const callWebWrapperRpc = (
  method: WebWrapperMethod,
  args: unknown[] = [],
): Promise<unknown> => {
  installRpcListener();

  const target = getRpcTargetWindow();
  if (!target) {
    return Promise.reject(
      new Error('WebWrapper RPC: lobby window unavailable'),
    );
  }

  return new Promise((resolve, reject) => {
    const id = `ww-rpc-${++rpcIdCounter}`;
    const timer = setTimeout(() => {
      pendingRpcCalls.delete(id);
      reject(new Error(`WebWrapper RPC timed out: ${method}`));
    }, WEB_WRAPPER_RPC.TIMEOUT_MS);

    pendingRpcCalls.set(id, { reject, resolve, timer });
    target.postMessage(
      {
        type: WEB_WRAPPER_RPC.CALL,
        id,
        method,
        args,
      },
      window.location.origin,
    );
  });
};

const callVoidWebWrapperRpc = (
  method: WebWrapperMethod,
  args: unknown[] = [],
): Promise<void> => callWebWrapperRpc(method, args).then(() => undefined);

const createRpcWebWrapper = (): WebWrapper => ({
  isWrapper: () => true,
  triggerLocateUser: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.TRIGGER_LOCATE_USER),
  triggerLocateUserWithDomain: (domain) =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.TRIGGER_LOCATE_USER_WITH_DOMAIN, [
      domain,
    ]),
  getSession: () =>
    callWebWrapperRpc(
      WEB_WRAPPER_METHODS.GET_SESSION,
    ) as Promise<WebWrapperSession | null>,
  getUserId: () =>
    callWebWrapperRpc(WEB_WRAPPER_METHODS.GET_USER_ID) as Promise<
      string | null
    >,
  getBalance: async () => {
    const result = await callWebWrapperRpc(WEB_WRAPPER_METHODS.GET_BALANCE);
    return Array.isArray(result) ? new Set<Balance>(result as Balance[]) : null;
  },
  getContext: () =>
    callWebWrapperRpc(
      WEB_WRAPPER_METHODS.GET_CONTEXT,
    ) as Promise<WebWrapperContext | null>,
  openDeposit: () => callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.OPEN_DEPOSIT),
  openResponsiblePlaySettings: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.OPEN_RESPONSIBLE_PLAY_SETTINGS),
  requestClose: () => callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_CLOSE),
  requestDetach: (payload) =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_DETACH, [payload]),
  notifyDetachReady: (payload) =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.NOTIFY_DETACH_READY, [payload]),
  requestReload: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_RELOAD),
  requestLogout: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_LOGOUT),
  requestKeepAlive: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_KEEP_ALIVE),
  requestIncentiveGameRedirect: (path) =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.REQUEST_INCENTIVE_GAME_REDIRECT, [
      path,
    ]),
  dismissRealityCheckModal: () =>
    callVoidWebWrapperRpc(WEB_WRAPPER_METHODS.DISMISS_REALITY_CHECK_MODAL),
});

export const getLobbyWebWrapper = (): WebWrapper | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const direct = getDirectLobbyWebWrapper();
  if (direct) {
    return direct;
  }

  if (isEmbeddedQueryRequested() && isEmbeddedIframe()) {
    cachedRpcWrapper ??= createRpcWebWrapper();
    return cachedRpcWrapper;
  }

  return null;
};

export const resolveWebWrapper = getLobbyWebWrapper;

export const isWebWrapperAvailable = (): boolean =>
  getLobbyWebWrapper() !== null;

/** @internal */
export const resetWebWrapperRpcForTests = (): void => {
  pendingRpcCalls.forEach(({ reject, timer }) => {
    clearTimeout(timer);
    reject(new Error('WebWrapper RPC reset'));
  });
  pendingRpcCalls.clear();

  if (rpcListener && typeof window !== 'undefined') {
    window.removeEventListener('message', rpcListener);
  }

  cachedRpcWrapper = null;
  rpcIdCounter = 0;
  rpcListener = null;
};
